// App-level branding, selected by SUBDOMAIN — the pre-login level of the cascade:
//   SideNerd default -> app (this file) -> project ui.json -> client.
//
// ither.sidenerd.app brands the login page as Ither before anyone signs in;
// app.sidenerd.app (and localhost) get the SideNerd default. Local testing:
// add ?brand=ither to the URL.
//
// Registry is code for now (N is small, zero infra); it can migrate to published
// brand/{app}.json artifacts in sidenerd-config when products multiply.

import { brand } from "../brand";
import type { Branding } from "../config/types";

const LOGOS = "https://integrationlogos.s3.us-east-1.amazonaws.com";
const BRAND_ASSETS = "https://sidenerd-marketing-assets.s3.us-east-1.amazonaws.com/brand";

const products = brand.products;

export const SIDENERD_BRAND: Required<
  Pick<Branding, "appName" | "logoUrl" | "faviconUrl" | "accent" | "accentInk" | "onAccent">
> = {
  appName: "Side Nerd",
  logoUrl: `${LOGOS}/Side%20Nerd%20Logo.svg`,
  faviconUrl: "/favicon.png",
  accent: brand.color.accent,
  accentInk: brand.color.accentInk,
  onAccent: brand.color.onAccent,
};

// Keyed by the backend registry key (first segment of project_string). Colors
// come from brand.products — never hardcode hex values here (check:brand
// enforces both). Each product's PRIMARY is the one-accent app-shell color;
// its accent/secondary stay marketing highlights (see BRAND.md).
const APP_BRANDS: Record<string, Branding> = {
  ither: {
    appName: products.ither.displayName,
    logoUrl: `${LOGOS}/ither128.png`,
    faviconUrl: `${LOGOS}/ither128.png`,
    accent: products.ither.primary,
    accentInk: products.ither.primaryInk,
    onAccent: products.ither.onPrimary,
    tagline: "Volunteer & donation tracking",
    logsLabel: "Log Entries",
  },
  medimatch: {
    // Dark-teal chrome; orange (products.medimatch.accent) is the identifying
    // highlight and logo blue (products.medimatch.secondary) is for graphics —
    // neither drives the app shell.
    appName: products.medimatch.displayName,
    logoUrl: `${LOGOS}/MediMatchlogo.jpg`,
    faviconUrl: `${BRAND_ASSETS}/medimatch-icon.png`,
    accent: products.medimatch.primary,
    accentInk: products.medimatch.primaryInk,
    onAccent: products.medimatch.onPrimary,
    tagline: "Medicare plan matching",
    logsLabel: "Inquiries",
  },
  // app_name "network" = the Link Lead product (textlinklead.com).
  network: {
    appName: products.linklead.displayName,
    logoUrl: `${BRAND_ASSETS}/LinkLeadLogo.png`,
    faviconUrl: `${BRAND_ASSETS}/LinkLeadLogo.png`,
    accent: products.linklead.primary,
    accentInk: products.linklead.primaryInk,
    onAccent: products.linklead.onPrimary,
    tagline: "SMS lead capture for sales teams",
    logsLabel: "Contacts",
  },
};

// URL slugs that differ from the registry key: ?brand=linklead and
// linklead.sidenerd.app resolve to the same product as ?brand=network and
// network_* project strings. One registration + an alias — never a duplicate
// definition, which would drift.
const APP_ALIASES: Record<string, string> = {
  linklead: "network",
};

/** App key from a project_string ("medimatch_newhope" -> "medimatch"). Lives
 *  here rather than apps/reports.ts so public pages can brand by project without
 *  pulling in the report components. */
export function appKeyOf(projectString: string): string {
  return projectString.split("_")[0] ?? projectString;
}

/** Look up the app-level brand for an app key or alias (e.g. "ither", "linklead"). */
export function appBrandFor(appKey: string): Branding | undefined {
  const key = appKey.toLowerCase();
  return APP_BRANDS[APP_ALIASES[key] ?? key];
}

// Public slug per registry key — APP_ALIASES inverted, so links for "network"
// read linklead.sidenerd.app, matching the marketing name.
const PREFERRED_SLUGS: Record<string, string> = Object.fromEntries(
  Object.entries(APP_ALIASES).map(([slug, key]) => [key, slug]),
);

/**
 * The pinned single-product URL for an app key (e.g. "network" ->
 * "https://linklead.sidenerd.app/"): the exact experience of a client who has
 * only that product, because appFromHost() pins the brand on that subdomain.
 */
export function singleProductUrlFor(appKey: string): string {
  const key = appKey.toLowerCase();
  return `https://${PREFERRED_SLUGS[key] ?? key}.sidenerd.app/`;
}

/** "ither.sidenerd.app" -> "ither"; app.sidenerd.app / localhost -> null.
 *  Alias subdomains canonicalize (linklead.sidenerd.app -> "network") so
 *  downstream key matches (project lookup, reports registry) never see the
 *  alias spelling. */
export function appFromHost(hostname: string = window.location.hostname): string | null {
  const m = hostname.toLowerCase().match(/^([a-z0-9-]+)\.sidenerd\.app$/);
  if (m && m[1] !== "app") return APP_ALIASES[m[1]] ?? m[1];
  return null;
}

/** ?brand=ither override for local dev / demos. */
function appFromQuery(): string | null {
  return new URLSearchParams(window.location.search).get("brand");
}

/** The app-level brand for this page load (pre-login; host wins, query for dev).
 *  `fallbackAppKey` covers the multi-product hub (app.sidenerd.app), where
 *  appFromHost() is null by design: without it the app layer drops out of the
 *  cascade and app-level fields (logsLabel, tagline, faviconUrl) silently revert
 *  to the SideNerd defaults even though a product is selected. Callers with no
 *  product yet (LoginPage) omit it and keep the host-only behaviour. */
export function getAppBrand(fallbackAppKey?: string): Branding {
  const key = appFromHost() ?? appFromQuery() ?? fallbackAppKey ?? null;
  return (key && appBrandFor(key)) || { ...SIDENERD_BRAND };
}

/** Full cascade merge: SideNerd default <- app <- project <- client. */
export function mergeBranding(...layers: Array<Branding | undefined>): Required<Pick<Branding, "appName" | "accent">> & Branding {
  const merged: Branding = {};
  for (const layer of layers) {
    if (!layer) continue;
    // Drop empty-string values so they don't mask lower layers.
    const clean = Object.fromEntries(Object.entries(layer).filter(([, v]) => v)) as Branding;
    // A layer that overrides `accent` must not inherit a lower layer's
    // accent-family companions: a mismatched ink/on-accent is worse than the
    // derived fallback in brandVars() / the global.css default.
    if (clean.accent) {
      delete merged.accentInk;
      delete merged.onAccent;
    }
    Object.assign(merged, clean);
  }
  return {
    appName: merged.appName ?? SIDENERD_BRAND.appName,
    accent: merged.accent ?? SIDENERD_BRAND.accent,
    ...merged,
  };
}

// The default SideNerd icon is dark blue; swap it for the white variant when
// the browser is in dark mode so it doesn't vanish against a dark tab strip.
// Per-app/project favicons are used as-is in both schemes.
let lastAppliedBrand: Branding | null = null;
const darkScheme = window.matchMedia("(prefers-color-scheme: dark)");
darkScheme.addEventListener("change", () => {
  if (lastAppliedBrand) applyBrandToDocument(lastAppliedBrand);
});

/**
 * Apply the merged brand to the document chrome: favicon (faviconUrl, falling
 * back to logoUrl, falling back to the built-in SideNerd icon) and tab title.
 */
export function applyBrandToDocument(b: Branding): void {
  lastAppliedBrand = b;
  let href = b.faviconUrl || b.logoUrl || "/favicon.png";
  if (href === "/favicon.png" && darkScheme.matches) href = "/favicon-dark.png";
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  if (link.href !== href) {
    link.type = href.endsWith(".svg") ? "image/svg+xml" : "image/png";
    link.href = href;
  }
  const title = b.appName ?? SIDENERD_BRAND.appName;
  if (document.title !== title) document.title = title;
}
